// src/pages/EmployeeListPage.js
import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { FiPlus, FiSearch, FiEdit2, FiBarChart2, FiCheckSquare, FiTrash2, FiUserX, FiUserCheck } from "react-icons/fi";
import {
  collection,
  getDocs,
  query,
  orderBy,
  deleteDoc,
  doc,
  updateDoc,
  serverTimestamp,
  deleteField,
} from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../context/AuthContext";
import styles from "../styles/EmployeeListPage.module.css";

const DEFAULT_AVATAR = "/default-avatar.png";

/** ✅ helper: determine terminated */
const isEmpTerminated = (emp) =>
  (emp.employmentStatus || (emp.active === false ? "Terminated" : "Active")) === "Terminated";

/** ✅ helper: stable sorting (Active first, then Fired) + name */
const sortEmployees = (arr) => {
  const copy = [...arr];
  copy.sort((a, b) => {
    const aTerm = isEmpTerminated(a);
    const bTerm = isEmpTerminated(b);
    if (aTerm !== bTerm) return aTerm ? 1 : -1;
    return (a.name || "").localeCompare(b.name || "");
  });
  return copy;
};

const EmployeeListPage = () => {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [deletingId, setDeletingId] = useState(null);
  const [updatingId, setUpdatingId] = useState(null);

  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");

  const { userRole, currentUser } = useAuth();

  useEffect(() => {
    const fetchEmployees = async () => {
      setLoading(true);
      setError("");
      try {
        const usersCol = collection(db, "users");
        const qUsers = query(usersCol, orderBy("name", "asc"));
        const querySnapshot = await getDocs(qUsers);

        const employeeList = querySnapshot.docs.map((d) => ({
          id: d.id,
          ...d.data(),
        }));

        setEmployees(sortEmployees(employeeList));
      } catch (err) {
        console.error("Error fetching employees:", err);
        setError("Failed to load employees.");
      } finally {
        setLoading(false);
      }
    };

    fetchEmployees();
  }, []);

  const canManageEmployees = userRole === "Manager" || userRole === "HR";
  const canFire = userRole === "Manager"; // Only Manager can fire/rehire

  const stats = useMemo(() => {
    const s = { total: employees.length, active: 0, fired: 0, roles: 0 };
    const roleSet = new Set();
    employees.forEach((e) => {
      if (isEmpTerminated(e)) s.fired += 1;
      else s.active += 1;
      if (e.role) roleSet.add(e.role);
    });
    s.roles = roleSet.size;
    return s;
  }, [employees]);

  const rolesPresent = useMemo(() => {
    const set = new Set(employees.map((e) => e.role).filter(Boolean));
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [employees]);

  const filteredEmployees = useMemo(() => {
    const term = search.trim().toLowerCase();
    return employees.filter((e) => {
      if (roleFilter !== "all" && (e.role || "") !== roleFilter) return false;
      const terminated = isEmpTerminated(e);
      if (statusFilter === "active" && terminated) return false;
      if (statusFilter === "fired" && !terminated) return false;
      if (term) {
        const hay = `${e.name || ""} ${e.email || ""}`.toLowerCase();
        if (!hay.includes(term)) return false;
      }
      return true;
    });
  }, [employees, search, roleFilter, statusFilter]);

  // ✅ Optional: protect special roles from being fired/deleted
  // عدّلها حسب نظامك (مثلاً: Owner / Admin / SuperAdmin)
  const protectedRoles = useMemo(() => new Set(["Owner", "Admin", "SuperAdmin"]), []);

  const handleDeleteEmployee = async (employeeId, employeeName) => {
    if (!canManageEmployees) {
      setError("You do not have permission to delete employees.");
      return;
    }

    // ✅ Prevent deleting self (optional but recommended)
    if (employeeId === currentUser?.uid || employeeId === currentUser?.id) {
      setError("You can't delete your own account.");
      return;
    }

    const ok = window.confirm(
      `Are you sure you want to permanently delete "${employeeName || "N/A"}"?\nThis action cannot be undone.`
    );
    if (!ok) return;

    setDeletingId(employeeId);
    setError("");
    try {
      const userDocRef = doc(db, "users", employeeId);
      await deleteDoc(userDocRef);
      setEmployees((prev) => prev.filter((emp) => emp.id !== employeeId));
    } catch (err) {
      console.error("Error deleting employee document:", err);
      setError(`Failed to delete ${employeeName || "employee"}. Please try again.`);
    } finally {
      setDeletingId(null);
    }
  };

  const handleFireEmployee = async (emp) => {
    if (!canFire) {
      setError("Only Managers can change employment status.");
      return;
    }

    // ✅ Prevent firing yourself
    if (emp?.uid && currentUser?.uid && emp.uid === currentUser.uid) {
      setError("You can't fire yourself.");
      return;
    }

    // ✅ Prevent firing protected roles (optional)
    if (emp?.role && protectedRoles.has(emp.role)) {
      setError(`You can't fire a protected role (${emp.role}).`);
      return;
    }

    // ✅ prevent parallel updates
    if (updatingId && updatingId !== emp.id) {
      setError("Another update is in progress. Please wait.");
      return;
    }

    const ok = window.confirm(
      `Stop (fire) "${emp.name || emp.email}" from working? They will be marked as Fired and treated as inactive.`
    );
    if (!ok) return;

    const reasonRaw = window.prompt("Enter a reason for termination (optional):", "");
    const reason = (reasonRaw || "").trim();

    setUpdatingId(emp.id);
    setError("");

    try {
      const refDoc = doc(db, "users", emp.id);

      await updateDoc(refDoc, {
        employmentStatus: "Terminated",
        active: false,
        terminatedAt: serverTimestamp(),
        terminatedBy: currentUser?.uid || "system",
        terminationReason: reason ? reason : deleteField(),
      });

      // ✅ Reflect immediately in UI + re-sort list
      setEmployees((prev) =>
        sortEmployees(
          prev.map((e) =>
            e.id === emp.id
              ? {
                  ...e,
                  employmentStatus: "Terminated",
                  active: false,
                  terminatedAt: new Date(), // local indicator only
                  terminatedBy: currentUser?.uid || "system",
                  terminationReason: reason || undefined,
                }
              : e
          )
        )
      );
    } catch (err) {
      console.error("Error firing employee:", err);
      setError("Failed to update employment status. Please try again.");
    } finally {
      setUpdatingId(null);
    }
  };

  const handleRehireEmployee = async (emp) => {
    if (!canFire) {
      setError("Only Managers can change employment status.");
      return;
    }

    // ✅ Prevent rehiring protected roles? عادة لا، بس خليها اختيارية:
    // if (emp?.role && protectedRoles.has(emp.role)) { ... }

    // ✅ prevent parallel updates
    if (updatingId && updatingId !== emp.id) {
      setError("Another update is in progress. Please wait.");
      return;
    }

    const ok = window.confirm(`Rehire "${emp.name || emp.email}"? They will be marked as Active again.`);
    if (!ok) return;

    setUpdatingId(emp.id);
    setError("");

    try {
      const refDoc = doc(db, "users", emp.id);

      await updateDoc(refDoc, {
        employmentStatus: "Active",
        active: true,
        terminatedAt: deleteField(),
        terminatedBy: deleteField(),
        terminationReason: deleteField(),
      });

      // ✅ Reflect immediately in UI + re-sort list
      setEmployees((prev) =>
        sortEmployees(
          prev.map((e) =>
            e.id === emp.id
              ? {
                  ...e,
                  employmentStatus: "Active",
                  active: true,
                  terminatedAt: undefined,
                  terminatedBy: undefined,
                  terminationReason: undefined,
                }
              : e
          )
        )
      );
    } catch (err) {
      console.error("Error rehiring employee:", err);
      setError("Failed to update employment status. Please try again.");
    } finally {
      setUpdatingId(null);
    }
  };

  const renderStatusBadge = (emp) => {
    const terminated = isEmpTerminated(emp);
    return (
      <span
        className={`${styles.statusBadge} ${terminated ? styles.statusTerminated : styles.statusActive}`}
        title={terminated && emp.terminationReason ? `Reason: ${emp.terminationReason}` : undefined}
      >
        {terminated ? "Fired" : "Active"}
      </span>
    );
  };

  // Safe date formatting for optional fields
  const fmtDate = (v) => {
    if (!v) return "-";
    try {
      if (typeof v === "string") {
        const d = new Date(v.includes("T") ? v : `${v}T00:00:00`);
        return isNaN(d) ? "-" : d.toLocaleDateString();
      }
      if (v?.toDate) return v.toDate().toLocaleDateString();
      if (v instanceof Date) return v.toLocaleDateString();
      return "-";
    } catch {
      return "-";
    }
  };

  // tiny avatar component with graceful fallback
  const Avatar = ({ name, photoURL, size = 36 }) => (
    <img
      src={photoURL || DEFAULT_AVATAR}
      alt={name || "User"}
      width={size}
      height={size}
      style={{
        width: size,
        height: size,
        borderRadius: "50%",
        objectFit: "cover",
        boxShadow: "0 0 0 2px #fff, 0 0 0 3px #e5e7eb",
      }}
      onError={(e) => {
        if (e.currentTarget.src !== DEFAULT_AVATAR) e.currentTarget.src = DEFAULT_AVATAR;
      }}
    />
  );

  return (
    <div className={styles.pageContainer}>
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <h2 className={styles.title}>Employees</h2>
          <p className={styles.subtitle}>Manage staff accounts, roles and employment status.</p>
        </div>
        {canManageEmployees && (
          <Link to="/employees/new" className={styles.addButton}>
            <FiPlus /> Add Employee
          </Link>
        )}
      </div>

      {error && <div className={styles.errorMessage}>{error}</div>}

      {/* Stats */}
      <div className={styles.statsRow}>
        <div className={`${styles.statCard} ${styles.statTotal}`}>
          <div className={styles.statLabel}>Total Staff</div>
          <div className={styles.statValue}>{stats.total}</div>
        </div>
        <div className={`${styles.statCard} ${styles.statActive}`}>
          <div className={styles.statLabel}>Active</div>
          <div className={styles.statValue}>{stats.active}</div>
        </div>
        <div className={`${styles.statCard} ${styles.statFired}`}>
          <div className={styles.statLabel}>Fired</div>
          <div className={styles.statValue}>{stats.fired}</div>
        </div>
        <div className={`${styles.statCard} ${styles.statRoles}`}>
          <div className={styles.statLabel}>Roles</div>
          <div className={styles.statValue}>{stats.roles}</div>
        </div>
      </div>

      {/* Toolbar */}
      <div className={styles.toolbar}>
        <div className={styles.searchBox}>
          <FiSearch className={styles.searchIcon} />
          <input
            type="text"
            placeholder="Search by name or email…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className={styles.searchInput}
          />
        </div>
        <select className={styles.filterSelect} value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)}>
          <option value="all">All roles</option>
          {rolesPresent.map((r) => (
            <option key={r} value={r}>{r}</option>
          ))}
        </select>
        <select className={styles.filterSelect} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          <option value="all">All statuses</option>
          <option value="active">Active</option>
          <option value="fired">Fired</option>
        </select>
      </div>

      {loading ? (
        <div className={styles.loadingIndicator}>Loading employees...</div>
      ) : employees.length === 0 ? (
        <div className={styles.emptyState}>
          <FiSearch className={styles.emptyIcon} />
          <p>No employees found.</p>
        </div>
      ) : filteredEmployees.length === 0 ? (
        <div className={styles.emptyState}>
          <FiSearch className={styles.emptyIcon} />
          <p>No employees match your filters.</p>
        </div>
      ) : (
        <div className={styles.tableContainer}>
          <table className={styles.employeeTable}>
            <thead className={styles.tableHeader}>
              <tr>
                <th>Employee</th>
                <th>Role</th>
                <th>Status</th>
                <th>Phone</th>
                <th>Date of Birth</th>
                <th className={styles.actionsHeader}>Actions</th>
              </tr>
            </thead>

            <tbody className={styles.tableBody}>
              {filteredEmployees.map((emp) => {
                const terminated = isEmpTerminated(emp);

                // ✅ UI protection hints
                const isSelf = emp?.uid && currentUser?.uid && emp.uid === currentUser.uid;
                const isProtected = emp?.role && protectedRoles.has(emp.role);

                return (
                  <tr
                    key={emp.id}
                    className={`${deletingId === emp.id ? styles.deletingRow : ""} ${terminated ? styles.terminatedRow : ""}`}
                  >
                    <td data-label="Employee">
                      <div className={styles.personCell}>
                        <Avatar name={emp.name || emp.email} photoURL={emp.photoURL} />
                        <div className={styles.personText}>
                          <div className={styles.personName}>{emp.name || "N/A"}</div>
                          <div className={styles.personEmail}>{emp.email || "-"}</div>
                        </div>
                      </div>
                    </td>

                    <td data-label="Role">
                      {emp.role ? <span className={styles.roleChip}>{emp.role}</span> : <span className={styles.muted}>N/A</span>}
                    </td>
                    <td data-label="Status">{renderStatusBadge(emp)}</td>
                    <td data-label="Phone">{emp.phone || emp.phoneNumber || <span className={styles.muted}>—</span>}</td>
                    <td data-label="Date of Birth">{fmtDate(emp.dateOfBirth)}</td>

                    <td data-label="Actions" className={styles.actionsCell}>
                      {canManageEmployees && (
                        <Link
                          to={`/employees/${emp.id}/edit`}
                          className={`${styles.actionButton} ${styles.editButton}`}
                          title={`Edit ${emp.name || emp.email}`}
                        >
                          <FiEdit2 />
                        </Link>
                      )}

                      {userRole === "Manager" && (
                        <Link
                          to={`/employees/${emp.id}/report`}
                          className={`${styles.actionButton} ${styles.reportButton}`}
                          title={`Report for ${emp.name || emp.email}`}
                        >
                          <FiBarChart2 />
                        </Link>
                      )}

                      {userRole === "Manager" && (
                        <Link
                          to={`/employees/${emp.id}/tasks`}
                          className={`${styles.actionButton} ${styles.viewTasksButton}`}
                          title={`View tasks for ${emp.name || emp.email}`}
                        >
                          <FiCheckSquare />
                        </Link>
                      )}

                      {/* ✅ Fire / Rehire with safer disabling */}
                      {canFire && !terminated && (
                        <button
                          onClick={() => handleFireEmployee(emp)}
                          className={`${styles.actionButton} ${styles.fireButton}`}
                          disabled={updatingId === emp.id || !!updatingId || isSelf || isProtected}
                          title={
                            isSelf
                              ? "You can't fire yourself"
                              : isProtected
                              ? `Protected role (${emp.role})`
                              : `Fire ${emp.name || emp.email}`
                          }
                        >
                          <FiUserX /> {updatingId === emp.id ? "…" : "Fire"}
                        </button>
                      )}

                      {canFire && terminated && (
                        <button
                          onClick={() => handleRehireEmployee(emp)}
                          className={`${styles.actionButton} ${styles.rehireButton}`}
                          disabled={updatingId === emp.id || !!updatingId}
                          title={`Rehire ${emp.name || emp.email}`}
                        >
                          <FiUserCheck /> {updatingId === emp.id ? "…" : "Rehire"}
                        </button>
                      )}

                      {/* Delete */}
                      {canManageEmployees && (
                        <button
                          onClick={() => handleDeleteEmployee(emp.id, emp.name || emp.email)}
                          className={`${styles.actionButton} ${styles.deleteButton}`}
                          disabled={deletingId === emp.id || !!deletingId || isSelf || isProtected}
                          title={
                            isSelf
                              ? "You can't delete yourself"
                              : isProtected
                              ? `Protected role (${emp.role})`
                              : `Delete ${emp.name || emp.email}`
                          }
                        >
                          <FiTrash2 />
                        </button>
                      )}

                      {!canManageEmployees && userRole !== "Manager" && (
                        <span className={styles.noActions}>—</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default EmployeeListPage;
